import { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Loader2, AlertTriangle, RefreshCw } from 'lucide-react';
import { API_BASE } from '../lib/api';
import SentimentChart from './dashboard/SentimentChart';
import ThemesDisplay from './dashboard/ThemesDisplay';
import BlindspotCards from './dashboard/BlindspotCards';
import EngagementDonut from './dashboard/EngagementDonut';
import ReflectivePrompts from './dashboard/ReflectivePrompts';
import MentalModels from './dashboard/MentalModels';

// Define the structure of our dashboard data for type safety
interface DashboardData {
  coaching_themes: { title: string; data: string[] };
  sentiment_trend: { title: string; data: { date: string; score: number }[] };
  blindspot_alerts: { title: string; data: { type: string; alert: string }[] };
  coach_engagement_mix: { title: string; data: Record<string, number> };
  reflective_questions: {
    title: string;
    data: { prompt: string; why_it_matters: string; micro_action: string }[];
  };
  mental_models: {
    title: string;
    data: { name: string; summary: string; how_to_apply: string }[];
  };
}

export default function InsightsTab() {
  const { getToken } = useAuth();
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true); // Start in loading state
  const [error, setError] = useState<string | null>(null);

  const fetchDashboardData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const token = await getToken();
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`${API_BASE}/analytics-dashboard`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch dashboard data.');
      }

      const raw = await response.json();

      if (raw?.message && !raw.coaching_themes) {
        setDashboardData(null);
        setError(raw.message);
        return;
      }

      const normalized: DashboardData = {
        coaching_themes: raw?.coaching_themes ?? { title: 'Top Coaching Themes', data: [] },
        sentiment_trend: raw?.sentiment_trend ?? { title: 'Sentiment Trendline', data: [] },
        blindspot_alerts: raw?.blindspot_alerts ?? { title: 'Blindspot Alerts', data: [] },
        coach_engagement_mix: raw?.coach_engagement_mix ?? { title: 'Coach Engagement Mix', data: {} },
        reflective_questions:
          raw?.reflective_questions ?? {
            title: 'Reflective Nudges',
            data: [],
          },
        mental_models:
          raw?.mental_models ?? {
            title: 'Mental Models to Apply',
            data: [],
          },
      };

      setDashboardData(normalized);

    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Automatically fetch data on initial load
  useEffect(() => {
    fetchDashboardData();
  }, []);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="ml-4 text-slate-600">Generating your Growth Dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full flex-col items-center justify-center space-y-4 text-center">
        <div className="flex items-center gap-2 text-amber-600">
          <AlertTriangle className="h-6 w-6" />
          <span className="font-semibold">{error}</span>
        </div>
        <button
          type="button"
          onClick={fetchDashboardData}
          className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:border-primary hover:text-primary"
        >
          <RefreshCw className="h-4 w-4" />
          Retry insight generation
        </button>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="text-center">
        <h2 className="text-2xl font-bold">No data available</h2>
        <p className="text-slate-500">Complete a coaching session to see your dashboard.</p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6">
      <div className="space-y-2">
        <h1 className="text-3xl font-bold text-slate-900">My Growth Dashboard</h1>
        <p className="max-w-3xl text-sm text-muted-foreground">
          Insights distilled from your recent coaching conversations. Use the reflective prompts and mental models below to turn awareness into forward motion.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <SentimentChart data={dashboardData.sentiment_trend.data} />
        <ThemesDisplay data={dashboardData.coaching_themes.data} />
        <BlindspotCards data={dashboardData.blindspot_alerts.data} />
        <EngagementDonut data={dashboardData.coach_engagement_mix.data} />
        <ReflectivePrompts
          title={dashboardData.reflective_questions.title}
          data={dashboardData.reflective_questions.data}
          className="lg:col-span-2"
        />
        <MentalModels
          title={dashboardData.mental_models.title}
          data={dashboardData.mental_models.data}
          className="lg:col-span-2"
        />
      </div>
    </div>
  );
}
